'use client';

/**
 * **내 화면** 목록을 읽고 쓴다 — 워크스페이스 문서의 `menuSets` 한 필드.
 *
 * ```
 *   CatchMomentsWorkspace/{id}.menuSets
 *     │ workspace.byId (실시간 구독)
 *   이 훅          ← 읽기·이름·끄기·차례·필터를 한 자리에
 *     ▼
 *   추적 관리의 「내 화면」 구역 · 브랜드 멘션 화면(`?view=`)
 * ```
 *
 * 🔑 사이드바는 이 훅을 **안 쓴다** — 메뉴는 이미 실려 온 워크스페이스 문서에서 바로 읽는다
 *   (`nav-config-service`). 여기는 고치는 화면의 자리다.
 * ⚠️ **구독이다** — 다른 팀원이 줄을 고치면 여기도 따라 바뀐다.
 * ⚠️ 새 창구를 만들지 않았다 — 범용 `workspace.fields.update` 로 그 필드만 갈아 끼운다
 *   (허브의 즐겨찾기와 같은 길이다).
 * 🚨 저장은 **목록 통째**다 — 한 줄만 보내면 나머지가 지워진다.
 */

import type { MenuSet, MenuSetFilter } from 'src/utils/workspace/menu-sets';

import { useMemo, useState, useEffect, useCallback } from 'react';

import { useUser } from 'src/hooks/use-user';

import {
  addMenuSet,
  moveMenuSet,
  swapMenuSets,
  MENU_SET_MAX,
  parseMenuSets,
  renameMenuSet,
  removeMenuSet,
  toggleMenuSet,
  nextMenuSetId,
  defaultMenuSets,
  setMenuSetFilter,
  syncCampaignSets,
  restoreBuiltinSets,
  setMenuSetDashboard,
} from 'src/utils/workspace/menu-sets';

import { fsCall, fsSubscribe } from 'src/actions/firestore/dispatch';

export function useMenuSets() {
  const user = useUser();
  const workspaceId = String(user?.lastWorkspace ?? '').trim();

  const [sets, setSets] = useState<MenuSet[]>(defaultMenuSets);
  /**
   * 수집 세트 목록 — **줄이 세트마다 하나**라 목록이 바뀌면 줄도 따라 바뀐다.
   * ⚠️ 저장본과 맞추는 것은 `syncCampaignSets` 다 — 여기서는 들고만 있는다.
   */
  /**
   * 살아 있는 수집 세트 문서 — **통째로** 든다(`accounts` 까지).
   * 🚨 워크스페이스의 `campaignList` 는 세션이 실어 온 **파생 스냅숏**이라 계정이 빠져 있다 —
   *   그것으로 갈래를 재면 계정만 건 세트가 멘션으로 떨어진다(사이드바가 그랬다).
   */
  const [campaigns, setCampaigns] = useState<Record<string, any>[]>([]);
  const [ready, setReady] = useState(false);
  /** 세트 문서를 한 번이라도 받았나 — 「세트 없음」과 「아직」을 가른다 */
  const [campaignsReady, setCampaignsReady] = useState(false);
  /** 살아 있는 워크스페이스 문서 — 방금 고친 설정이 화면을 옮기지 않아도 온다 */
  const [workspace, setWorkspace] = useState<Record<string, any> | null>(null);
  /** 지금 저장 중인 줄 — 그 줄만 도는 표시를 낸다 */
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!workspaceId) {
      setSets(defaultMenuSets());
      setWorkspace(null);
      setReady(true);
      return undefined;
    }
    setReady(false);
    setCampaignsReady(false);
    setWorkspace(null);
    const unsubs = [
      fsSubscribe('workspace.byId', { workspaceId }, {
        onChange: (doc) => {
          setSets(parseMenuSets((doc as Record<string, unknown> | null)?.menuSets));
          setWorkspace((doc as Record<string, any> | null) ?? null);
          setReady(true);
        },
      }),
      fsSubscribe('keywordCampaign.byWorkspace', { workspaceId }, {
        onChange: (rows) => {
          setCampaigns(Array.isArray(rows) ? (rows as Record<string, any>[]) : []);
          setCampaignsReady(true);
        },
      }),
    ];
    return () => {
      unsubs.forEach((unsub) => {
        try {
          unsub?.();
        } catch {
          /* no-op */
        }
      });
    };
  }, [workspaceId]);

  /*
    🚨 **고치는 것은 «맞춘 목록»이다** — 저장본에는 수집 세트 줄이 아직 없을 수 있어(읽을 때
      만들어진다), 저장본을 보고 고치면 그 줄을 못 찾아 「더 옮길 수 없습니다」로 막힌다.
      맞춘 목록을 고쳐 저장하면 첫 조작에서 세트 줄들이 그대로 굳는다.
  */
  const synced = useMemo(() => syncCampaignSets(sets, campaigns), [campaigns, sets]);

  /**
   * 목록을 통째로 저장한다 — 화면은 응답을 안 기다리고 **먼저 바뀐다**(구독이 곧 같은 값을 실어 온다).
   * ⚠️ 실패하면 저장 전 목록으로 되돌린다 — 바뀐 것처럼 보이는 채로 두면 다음에 열었을 때 사라진다.
   */
  const persist = useCallback(
    async (next: MenuSet[], key: string): Promise<boolean> => {
      if (!workspaceId || saving) return false;
      const before = synced;
      setSaving(key);
      setError('');
      setSets(next);
      const res = await fsCall('workspace.fields.update', { workspaceId, payload: { menuSets: next } });
      setSaving('');
      if (!res?.ok) {
        setSets(before);
        setError(res?.error?.message || '저장하지 못했습니다.');
        return false;
      }
      return true;
    },
    [saving, synced, workspaceId],
  );

  /** 바뀐 것이 없으면 저장하지 않는다 — 순수 함수가 «같은 배열»을 돌려주면 막힌 것이다 */
  const apply = useCallback(
    (next: MenuSet[], key: string, blocked: string): Promise<boolean> => {
      if (next === synced) {
        setError(blocked);
        return Promise.resolve(false);
      }
      return persist(next, key);
    },
    [persist, synced],
  );

  const rename = useCallback(
    (id: string, name: string) => apply(renameMenuSet(synced, id, name), id, '이름을 적어 주세요.'),
    [apply, synced],
  );

  const toggle = useCallback((id: string) => persist(toggleMenuSet(synced, id), id), [persist, synced]);

  /**
   * 두 줄의 자리를 맞바꾼다 — **구역 안에서** 한 칸 옮길 때 쓴다.
   * 🔑 구역이 갈린 목록에서 「한 칸 위」는 배열의 바로 옆이 아니다(`swapMenuSets` 머리말).
   */
  const swap = useCallback(
    (idA: string, idB: string) => apply(swapMenuSets(synced, idA, idB), idA, '더 옮길 수 없습니다.'),
    [apply, synced],
  );

  const move = useCallback(
    (id: string, delta: number) => apply(moveMenuSet(synced, id, delta), id, '더 옮길 수 없습니다.'),
    [apply, synced],
  );

  const remove = useCallback(
    (id: string) => apply(removeMenuSet(synced, id), id, '그 줄을 찾지 못했습니다.'),
    [apply, synced],
  );

  /** 빠진 기본 화면을 도로 세운다 — 기본 셋은 만들기 폼으로 다시 만들 수 없다 */
  const restoreBuiltins = useCallback(
    () => apply(restoreBuiltinSets(synced), '', '되돌릴 기본 화면이 없습니다.'),
    [apply, synced],
  );

  /**
   * 새 줄을 만든다.
   * 🚨 **수집을 걸지 않는다** — 이미 모인 것을 이 조건으로 보는 화면이다(`menu-sets` 머리말).
   * @returns 만든 줄의 열쇠 — 실패면 빈 글
   */
  const add = useCallback(
    async (input: {
      name: string;
      keywords?: string[];
      accounts?: MenuSet['accounts'];
      campaignId?: string;
      /** 화면에서 좁혀 둔 필터 — 만들 때부터 걸린 채로 선다 */
      filter?: Partial<MenuSetFilter>;
      /** 화면에서 꾸며 둔 대시보드 차례 — 만들 때부터 그대로 선다 */
      dashboard?: MenuSet['dashboard'];
    }): Promise<string> => {
      const id = nextMenuSetId(synced);
      const next = addMenuSet(synced, {
        id,
        name: input.name,
        ...(input.filter ? { filter: input.filter as MenuSetFilter } : {}),
        ...(input.dashboard ? { dashboard: input.dashboard } : {}),
        ...(input.campaignId
          ? { source: { kind: 'campaign' as const, campaignId: input.campaignId } }
          : { source: { kind: 'own' as const }, keywords: input.keywords, accounts: input.accounts }),
      });
      if (next === synced) {
        setError(synced.length >= MENU_SET_MAX ? `${MENU_SET_MAX}개까지 만들 수 있습니다.` : '이름을 적어 주세요.');
        return '';
      }
      return (await persist(next, id)) ? id : '';
    },
    [persist, synced],
  );

  /** 미리 지정해 둘 필터를 갈아 끼운다 — 축 셋뿐이다(기간은 화면이 쥔다) */
  const setFilter = useCallback(
    (id: string, filter: Partial<MenuSetFilter>) => persist(setMenuSetFilter(synced, id, filter), id),
    [persist, synced],
  );

  /**
   * 이 줄의 **대시보드 차례를 저장한다**.
   * 🔑 조건·필터는 안 건드린다 — 같은 조건 위에 무엇을 어떤 차례로 그릴지만 바뀐다.
   * 🔑 `null` 을 주면 기본(브랜드 멘션 그대로)으로 되돌린다.
   */
  const setDashboard = useCallback(
    (id: string, next: MenuSet['dashboard']) => persist(setMenuSetDashboard(synced, id, next), id),
    [persist, synced],
  );

  return {
    workspaceId, sets: synced, ready, saving, error,
    campaigns, campaignsReady, workspace,
    rename, toggle, move, swap, remove, restoreBuiltins, add, setFilter, setDashboard,
  };
}
